import React, { useRef } from 'react';
import { Plus } from 'lucide-react';
import FileActions from '../../Components/Common/Files/FileActions';
import { useTranslation } from '../../context/Internationalization';
import { brandColorsOptions } from '../../utility/Enum';
import { Avatar, AvatarFallback, AvatarImage } from '../../shadcn/components/ui/avatar';
import { Button } from '../../shadcn/components/ui/button';
import { FieldDescription, FieldError, FieldLabel } from '../../shadcn/components/ui/field';
import { RadioGroup, RadioGroupItem } from '../../shadcn/components/ui/radio-group';
import { isRecoveringRegistration } from './registrationProgress';
import AuthTextField from '../Login/AuthTextField';

export default function RegistrationClubFields({ values, errors, busy, progress, logoPreview, onChange, onLogo, onRemoveLogo }) {
  const { t } = useTranslation();
  const fileInput = useRef(null);
  const recovering = isRecoveringRegistration(progress);
  const uncertain = ['creating', 'uncertain'].includes(progress?.phase);
  const uploadDisabled = Boolean(busy || uncertain || (progress?.clubId && !progress.logoWanted) || progress?.logoStatus === 'saved');
  const chooseLogo = () => fileInput.current?.click();
  return <div className="flex flex-col gap-4">
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center gap-4">
        <Button type="button" variant="ghost" onClick={chooseLogo} disabled={uploadDisabled}
          aria-label={t('registration_upload_logo_action')} aria-describedby="club-logo-hint"
          className="size-auto rounded-full p-0 hover:bg-transparent">
          <Avatar className="size-12">
            {logoPreview && <AvatarImage src={logoPreview} alt={t('registration_logo_preview')} />}
            <AvatarFallback><Plus aria-hidden="true" /></AvatarFallback>
          </Avatar>
        </Button>
        <input ref={fileInput} type="file" hidden disabled={uploadDisabled} accept="image/png,image/jpeg,image/webp"
          aria-label={t('registration_upload_logo')}
          onChange={(event) => {
            if (event.target.files?.[0]) onLogo(event.target.files[0]);
            event.target.value = '';
          }} />
        <Button variant="outline" size="sm" type="button" onClick={chooseLogo}
          aria-invalid={Boolean(errors.logo)} aria-describedby="club-logo-hint"
          disabled={uploadDisabled}>
          {t('registration_upload_logo')}
        </Button>
        {logoPreview && <FileActions file={{ url: logoPreview, name: t('registration_logo_preview'), type: "image" }} />}
        {(logoPreview || progress?.logoWanted) && <Button variant="ghost" size="sm" type="button" disabled={busy || uncertain}
          onClick={onRemoveLogo}>{t('registration_remove_logo')}</Button>}
      </div>
      <FieldDescription id="club-logo-hint" role={errors.logo ? 'alert' : undefined}
        className={errors.logo ? 'text-destructive' : undefined}>{t(errors.logo || 'registration_logo_hint')}</FieldDescription>
    </div>
    <AuthTextField required label={t('registration_club_name')} name="name" autoComplete="organization"
      value={values.name} disabled={busy || recovering} onChange={(event) => onChange('name', event.target.value)}
      error={Boolean(errors.name)} helperText={errors.name ? t(errors.name) : ''} inputProps={{ maxLength: 100 }} />
    <div className="flex flex-col gap-2">
      <FieldLabel id="club-color-label">{t('registration_color')}</FieldLabel>
      <RadioGroup aria-labelledby="club-color-label" name="primaryColor" value={values.primaryColor.toUpperCase()}
        disabled={busy || recovering} onValueChange={(value) => onChange('primaryColor', value)}
        className="flex flex-wrap gap-2">
        {brandColorsOptions(t).map((option) => <RadioGroupItem key={option.value} value={option.value.toUpperCase()}
          aria-label={option.text} aria-invalid={Boolean(errors.primaryColor)}
          aria-describedby={errors.primaryColor ? 'club-color-error' : undefined}
          style={{ backgroundColor: option.value }}
          className="size-7 after:hidden border-2 border-background ring-1 ring-border data-checked:ring-2 data-checked:ring-foreground data-checked:ring-offset-2 data-checked:ring-offset-background [&_[data-slot=radio-group-indicator]>span]:hidden" />)}
      </RadioGroup>
      {errors.primaryColor && <FieldError id="club-color-error">{t(errors.primaryColor)}</FieldError>}
    </div>
  </div>;
}
